import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Form,
  Link,
  MessagePlugin,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'tdesign-react';
import ColTip from '@/components/Tip';
import { fetchServiceList } from '@/slices/resourceSlice';
import { deleteK8s, getK8sList, validateK8s } from '@/services/resourceService';
import useTable from '@/hooks/useTable';
import PermWrapper from '@/components/PermWrapper';
import { renderDateTime, toTsOptions } from '@/utils/common';
import FormModal from './FormModal';

const { FormItem } = Form;

function K8sTable({ fetchServiceList, serviceOptions }) {
  const [visible, setVisible] = useState();

  const columns = [
    {
      colKey: 'envName',
      title: '环境名称',
      width: 180,
      fixed: 'left',
    },
    {
      colKey: 'kubeName',
      title: 'k8s集群显示名称',
      width: 180,
    },
    {
      colKey: 'configClusterName',
      title: (
        <ColTip
          content={
            <>
              <strong>k8s集群实际名称</strong>
              <li>kube config中显示的ClusterName。</li>
            </>
          }
        >
          k8s集群实际名称
        </ColTip>
      ),
      width: 180,
    },
    {
      colKey: 'serviceUrl',
      title: 'kubectl连接地址',
      width: 280,
    },
    {
      colKey: 'isVerified',
      title: '验证状态',
      cell: ({ row }) =>
        row.isVerified === 1 ? (
          <Tag theme='success' variant='outline' size='small'>
            已验证
          </Tag>
        ) : (
          <Tag theme='danger' variant='outline' size='small'>
            未验证
          </Tag>
        ),
      width: 140,
    },
    {
      colKey: 'namespace',
      title: (
        <ColTip
          content={
            <>
              <strong>Operator Namespace</strong>
              <li>指定Operator部署的K8s namespace</li>
              <li>Operator在K8s集群中只需部署一次。</li>
            </>
          }
        >
          Operator Namespace
        </ColTip>
      ),
      width: 200,
    },
    {
      colKey: 'desc',
      title: '备注',
      width: 200,
    },
    {
      title: '创建时间',
      colKey: 'createTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      title: '更新时间',
      colKey: 'updateTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space>
          <PermWrapper resource='pulsar:resource:k8sCluster:validate'>
            <Link
              theme='primary'
              onClick={() => {
                handleVerify(row.id);
              }}
              hover='color'
            >
              验证
            </Link>
          </PermWrapper>
          <PermWrapper resource='pulsar:resource:k8sCluster:delete'>
            <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(row)}>
              <Link theme='danger' hover='color'>
                删除
              </Link>
            </Popconfirm>
          </PermWrapper>
        </Space>
      ),
      width: 130,
    },
  ];

  const loadData = useMemoizedFn(async (params) => {
    const { data } = await getK8sList(params);
    setData(data.items, data.count);
  });

  const { header, config, setData, doSearch, doRefresh } = useTable({
    columns,
    remote: true,
    search: (
      <FormItem name='envId'>
        <Select clearable filterable placeholder='选择环境' options={toTsOptions(serviceOptions)} />
      </FormItem>
    ),
    operators: (
      <PermWrapper resource='pulsar:resource:k8sCluster:create'>
        <Button
          theme='primary'
          onClick={() => {
            setVisible(true);
          }}
        >
          新建K8S
        </Button>
      </PermWrapper>
    ),
    onSearch: loadData,
  });

  const doDelete = async ({ id }) => {
    try {
      await deleteK8s(id);
      MessagePlugin.success('删除成功');
      doRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    }
  };

  const handleVerify = async (id) => {
    let tip;
    try {
      tip = MessagePlugin.loading({
        content: '验证中...',
        duration: 0,
      });
      await validateK8s(id);
      MessagePlugin.success('验证通过！');
      doRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      MessagePlugin.close(tip);
    }
  };

  useEffect(() => {
    doSearch();
    fetchServiceList();
  }, [doSearch, fetchServiceList]);

  const handleClose = (refresh) => {
    setVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <>
      {header}
      <Table {...config} rowKey='id' />
      <FormModal visible={visible} handleClose={handleClose} />
    </>
  );
}

export default connect(
  (state) => ({
    serviceOptions: state.resource.serviceOptions,
  }),
  {
    fetchServiceList,
  },
)(K8sTable);
